class GiftCardCart {
  constructor() {
    this.isProcessing = false;
    this.lastCartState = null;
    this.debug = false; // Disabilita per produzione
    this.originalAddToCart = null;
    this.mcShopifyIntercepted = false;
    this.validationEnabled = this.checkIfValidationEnabled();
    this.translations = this.getTranslations();
    // Cache per cart e risultati isGiftCard
    this.cartCache = null;
    this.cartCacheTime = 0;
    this.CACHE_TTL = 2000; // 2 secondi
    this.giftCardCache = new Map(); // Cache risultati isGiftCard per item key
    
    // Log delle traduzioni per debug
    console.log('[GiftCardCart] Traduzioni caricate:', {
      oneGiftCardOnly: this.translations.oneGiftCardOnly,
      giftCardAlreadyInCart: this.translations.giftCardAlreadyInCart
    });
    
    // Solo se la validazione è abilitata
    if (this.validationEnabled) {
      this.init();
    } else {
      console.log('[GiftCardCart] Validazione Gift Card disabilitata');
    }
  }
  
  getTranslations() {
    // Ottieni le traduzioni dal theme object o usa i default
    const defaultTranslations = {
      cartConflictTitle: 'Attenzione: Gift Card nel carrello',
      cartConflictMessage: 'Le Gift Card devono essere acquistate separatamente dagli altri prodotti. Per procedere con l\'ordine, scegli una delle seguenti opzioni:',
      removeRegularProducts: 'Rimuovi i prodotti normali e procedi con le Gift Card',
      removeGiftCards: 'Rimuovi le Gift Card e procedi con gli altri prodotti',
      cannotAddGiftCard: 'Non puoi aggiungere Gift Card quando hai altri prodotti nel carrello',
      cannotAddRegular: 'Non puoi aggiungere prodotti normali quando hai Gift Card nel carrello',
      oneGiftCardOnly: 'È possibile acquistare solo una Gift Card per ordine',
      giftCardAlreadyInCart: 'Hai già una Gift Card nel carrello',
      resolveConflict: 'Risolvi prima il conflitto nel carrello: le Gift Card devono essere acquistate separatamente',
      validationFailed: 'Le Gift Card devono essere acquistate separatamente',
      mixedCartWarning: 'Attenzione: Le Gift Card non possono essere acquistate insieme ad altri prodotti. Vai al carrello per risolvere il conflitto.',
      goToCart: 'Vai al carrello',
      close: 'Chiudi',
      completePurchaseFirst: 'Completa prima l\'acquisto o svuota il carrello',
      completeGiftCardPurchase: 'Completa prima l\'acquisto delle Gift Card',
      resolveConflictBeforeCheckout: 'Risolvi il conflitto Gift Card prima di procedere',
      errorRemovingProducts: 'Errore nella rimozione dei prodotti',
      errorRemovingGiftCards: 'Errore nella rimozione delle gift card',
      productAdded: 'Prodotto aggiunto al carrello!',
      errorAddingToCart: 'Errore nell\'aggiunta al carrello'
    };
    
    // Se esistono traduzioni custom, mergele con i default per evitare valori undefined
    if (window.theme && window.theme.t && window.theme.t.giftCardValidation) {
      return {
        ...defaultTranslations,
        ...window.theme.t.giftCardValidation
      };
    }
    
    return defaultTranslations;
  }

  init() {
    
    // Verifica il carrello al caricamento della pagina
    this.checkCartOnLoad();
    
    // Intercetta McShopify quando diventa disponibile
    this.interceptMcShopify();
    
    // Intercetta anche i form tradizionali
    this.interceptFormSubmits();
    
    // Aggiungi listener per verifiche quando il carrello viene aggiornato
    this.observeCartChanges();
  }
  
  checkIfValidationEnabled() {
    // Controlla prima l'impostazione globale del tema
    if (window.theme && typeof window.theme.giftCardValidation !== 'undefined') {
      return window.theme.giftCardValidation;
    }
    
    // Controlla se c'è un data attribute specifico sulla pagina
    const buyButton = document.querySelector('[data-gift-card-validation]');
    if (buyButton) {
      const value = buyButton.dataset.giftCardValidation;
      return value !== 'false' && value !== '0';
    }
    
    // Default: abilitato
    return true;
  }

  log(message, data = null) {
    if (this.debug) {
      console.log(`[GiftCardCart] ${message}`, data || '');
    }
  }

  interceptMcShopify() {
    // Controlla periodicamente se McShopify è disponibile
    const checkInterval = setInterval(() => {
      if (window.McShopify && window.McShopify.addToCart && !this.mcShopifyIntercepted) {
        this.log('McShopify trovato! Intercettazione in corso...');
        
        // Salva il metodo originale
        this.originalAddToCart = window.McShopify.addToCart.bind(window.McShopify);
        
        // Sovrascrivi con il nostro metodo
        window.McShopify.addToCart = async (params) => {
          this.log('McShopify.addToCart intercettato!', params);
          
          // Valida prima di aggiungere
          const canAdd = await this.validateAddToCart(params);
          
          if (!canAdd) {
            this.log('Aggiunta bloccata per conflitto Gift Card');
            
            // Chiama onError se fornito
            if (params && params.onError) {
              params.onError({
                message: this.translations.validationFailed,
                description: this.translations.cartConflictMessage
              });
            }
            
            return Promise.reject('Gift Card validation failed');
          }
          
          // Procedi con l'aggiunta originale
          this.log('Procedo con aggiunta originale');
          return this.originalAddToCart(params);
        };
        
        this.mcShopifyIntercepted = true;
        clearInterval(checkInterval);
        this.log('McShopify.addToCart sovrascritto con successo');
      }
    }, 100);
    
    // Stop checking dopo 10 secondi
    setTimeout(() => clearInterval(checkInterval), 10000);
  }

  async validateAddToCart(params) {
    // Ricontrolla se la validazione è abilitata (potrebbe essere cambiata dinamicamente)
    if (!this.checkIfValidationEnabled()) {
      this.log('Validazione disabilitata, permetto aggiunta');
      return true;
    }
    
    try {
      const cart = await this.getCart();
      
      // Determina cosa si sta aggiungendo
      let isAddingGiftCard = false;
      let productInfo = null;
      
      if (params) {
        if (params.form) {
          productInfo = this.getProductInfoFromDOM();
          isAddingGiftCard = this.checkIfGiftCardProduct(productInfo);
        } else if (params.items && params.items.length > 0) {
          const item = params.items[0];

          // Carosello regalo: non usare querySelector globale (legge sempre la prima card del DOM)
          if (window.__cartGiftCarouselAdd) {
            productInfo = { type: 'Regular', title: null, handle: null, tags: null };
            isAddingGiftCard = false;
          } else {
            productInfo = this.getProductInfoFromDOM();
            isAddingGiftCard = this.checkIfGiftCardProduct(productInfo);
          }
        } else if (params.formData && window.__cartGiftCarouselAdd) {
          productInfo = { type: 'Regular', title: null, handle: null, tags: null };
          isAddingGiftCard = false;
        }
      }
      
      console.log('[GiftCardCart] === VALIDAZIONE ADD TO CART ===');
      console.log('[GiftCardCart] Prodotto che si sta aggiungendo:', {
        isGiftCard: isAddingGiftCard,
        productInfo: productInfo
      });
      
      // Controlla conflitti
      const cartHasGiftCards = this.hasGiftCards(cart);
      const cartHasRegularProducts = this.hasRegularProducts(cart);
      
      console.log('[GiftCardCart] Stato carrello attuale:', {
        hasGiftCards: cartHasGiftCards,
        hasRegularProducts: cartHasRegularProducts,
        itemsCount: cart.items?.length || 0
      });
      
      // Applica regole di validazione
      if (isAddingGiftCard) {
        console.log('[GiftCardCart] Sto aggiungendo una GIFT CARD');
        
        // Se si sta aggiungendo una Gift Card, controlla se ce ne sono già altre nel carrello
        if (cartHasGiftCards) {
          console.log('[GiftCardCart] BLOCCO: Gift Card già presente nel carrello');
          const message = this.translations.oneGiftCardOnly || 'È possibile acquistare solo una Gift Card per ordine';
          const subMessage = this.translations.giftCardAlreadyInCart || 'Hai già una Gift Card nel carrello';
          this.showNotification(
            `❌ ${message}. ${subMessage}.`,
            'error',
            false,
            true
          );
          return false;
        }
        // Controlla se ci sono prodotti normali
        if (cartHasRegularProducts) {
          console.log('[GiftCardCart] BLOCCO: Prodotti normali nel carrello, non posso aggiungere Gift Card');
          this.showNotification(
            `❌ ${this.translations.cannotAddGiftCard}. ${this.translations.completePurchaseFirst}.`,
            'error',
            false,
            true
          );
          return false;
        }
      } else {
        console.log('[GiftCardCart] Sto aggiungendo un PRODOTTO NORMALE');
        
        if (cartHasGiftCards) {
          console.log('[GiftCardCart] BLOCCO: Gift Card nel carrello, non posso aggiungere prodotti normali');
          this.showNotification(
            `❌ ${this.translations.cannotAddRegular}. ${this.translations.completeGiftCardPurchase}.`,
            'error',
            false,
            true
          );
          return false;
        }
      }
      
      if (cartHasGiftCards && cartHasRegularProducts) {
        this.showNotification(
          `⚠️ ${this.translations.resolveConflict}`,
          'warning',
          false,
          true
        );
        return false;
      }
      
      return true;
      
    } catch (error) {
      console.error('[GiftCardCart] Errore validazione:', error);
      return true; // In caso di errore, permetti
    }
  }

  getProductInfoFromDOM() {
    // Cerca informazioni sul prodotto nel DOM
    const info = {
      type: null,
      title: null,
      handle: null,
      tags: null
    };
    
    // Cerca il product type
    const typeElement = document.querySelector('[data-product-type], .product-type, [itemprop="category"]');
    if (typeElement) {
      info.type = typeElement.dataset?.productType || typeElement.textContent || '';
    }
    
    // Cerca nel JSON-LD
    const jsonLd = document.querySelector('script[type="application/ld+json"]');
    if (jsonLd) {
      try {
        const data = JSON.parse(jsonLd.textContent);
        if (data['@type'] === 'Product') {
          info.type = data.category || info.type;
          info.title = data.name || info.title;
        }
      } catch (e) {}
    }
    
    // Cerca nel titolo
    const titleElement = document.querySelector('h1, .product-title, [itemprop="name"]');
    if (titleElement) {
      info.title = titleElement.textContent;
    }
    
    // Cerca nell'URL
    info.handle = window.location.pathname;
    
    // Cerca data attributes del prodotto
    const productElement = document.querySelector('[data-product], [data-product-handle], .product');
    if (productElement) {
      info.handle = productElement.dataset?.productHandle || info.handle;
      info.tags = productElement.dataset?.productTags || '';
    }
    
    this.log('Info prodotto dal DOM:', info);
    return info;
  }

  checkIfGiftCardProduct(productInfo) {
    // Prima controlla se c'è il recipient form nella pagina (indicatore definitivo di Gift Card)
    const hasRecipientForm = document.querySelector('recipient-form') !== null;
    if (hasRecipientForm) {
      console.log('[GiftCardCart] Rilevato recipient-form - È una Gift Card');
      return true;
    }
    
    if (!productInfo) return false;
    
    const checks = [
      productInfo.type && productInfo.type.toLowerCase().includes('gift'),
      productInfo.title && productInfo.title.toLowerCase().includes('gift'),
      productInfo.handle && productInfo.handle.toLowerCase().includes('gift'),
      productInfo.tags && productInfo.tags.toLowerCase().includes('gift'),
      // Controlla anche se c'è "card" nel type/title/handle
      productInfo.type && productInfo.type.toLowerCase() === 'gift card',
      productInfo.type && productInfo.type === 'Gift Card',
      productInfo.title && productInfo.title.toLowerCase().includes('gift card')
    ];
    
    const isGiftCard = checks.some(check => check === true);
    
    console.log('[GiftCardCart] Check Gift Card dettagliato:', {
      hasRecipientForm,
      productType: productInfo.type,
      productTitle: productInfo.title,
      productHandle: productInfo.handle,
      productTags: productInfo.tags,
      checksResults: checks,
      finalResult: isGiftCard
    });
    
    return isGiftCard;
  }

  interceptFormSubmits() {
    // Intercetta submit dei form
    document.addEventListener('submit', async (e) => {
      const form = e.target;
      
      // Verifica se è un form di aggiunta al carrello
      if (form && (form.action?.includes('/cart/add') || form.querySelector('[name="add"]'))) {
        this.log('Form submit intercettato:', form.action);
        
        // Controlla se la validazione è abilitata per questo form
        const formContainer = form.closest('[data-gift-card-validation]');
        if (formContainer && formContainer.dataset.giftCardValidation === 'false') {
          this.log('Validazione disabilitata per questo form');
          return; // Lascia procedere normalmente
        }
        
        // Se McShopify non è disponibile, gestisci direttamente
        if (!window.McShopify || !this.mcShopifyIntercepted) {
          e.preventDefault();
          e.stopPropagation();
          
          const canAdd = await this.validateAddToCart({ form });
          
          if (canAdd) {
            // Procedi con il submit
            const formData = new FormData(form);
            
            try {
              const response = await fetch('/cart/add.js', {
                method: 'POST',
                body: formData
              });
              
              if (response.ok) {
                this.showNotification(`✅ ${this.translations.productAdded}`, 'success');
                setTimeout(() => {
                  window.location.reload();
                }, 1000);
              }
            } catch (error) {
              console.error('Errore aggiunta:', error);
              this.showNotification(this.translations.errorAddingToCart, 'error');
            }
          }
        }
      }
    }, true); // Usa capture phase
  }

  async checkCartOnLoad() {
    if (this.isProcessing) return;

    try {
      this.isProcessing = true;
      const cart = await this.getCart();
      this.log('Carrello caricato:', cart);

      // Controlla se il carrello è cambiato
      const cartHash = JSON.stringify(cart.items?.map(i => i.key + i.quantity));
      if (this.lastCartState === cartHash) {
        this.isProcessing = false;
        return;
      }
      this.lastCartState = cartHash;

      if (cart && cart.items && cart.items.length > 0) {
        this.validateCartAndShowUI(cart);
      } else {
        this.removeCartBlockUI();
      }
    } catch (error) {
      console.error('Errore nel controllo del carrello:', error);
    } finally {
      this.isProcessing = false;
    }
  }

  async getCart(forceRefresh = false) {
    const now = Date.now();

    // Usa la cache se valida
    if (!forceRefresh && this.cartCache && (now - this.cartCacheTime) < this.CACHE_TTL) {
      return this.cartCache;
    }

    try {
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      this.cartCache = await response.json();
      this.cartCacheTime = now;
      // Invalida cache isGiftCard quando il cart cambia
      this.giftCardCache.clear();
      return this.cartCache;
    } catch (error) {
      console.error('Errore nel recupero del carrello:', error);
      return null;
    }
  }

  invalidateCache() {
    this.cartCache = null;
    this.cartCacheTime = 0;
    this.giftCardCache.clear();
  }

  isGiftCard(item) {
    // Usa cache se disponibile
    const cacheKey = item.key || item.variant_id;
    if (cacheKey && this.giftCardCache.has(cacheKey)) {
      return this.giftCardCache.get(cacheKey);
    }

    // Controlla se il prodotto è una gift card
    const checks = [
      item.product_type === 'Gift Card',
      item.product_type === 'gift-card',
      item.product_type === 'gift_card',
      item.product_type === 'giftcard',
      item.product_type && item.product_type.toLowerCase().includes('gift'),
      item.handle && item.handle.includes('gift-card'),
      item.vendor && item.vendor.toLowerCase().includes('gift'),
      item.tags && item.tags.toLowerCase && item.tags.toLowerCase().includes('gift'),
      item.gift_card === true,
      item.properties && item.properties._is_gift === 'true',
      // Controlla anche le proprietà del recipient form
      item.properties && item.properties['Gift card recipient'] === 'yes',
      item.properties && item.properties['Gift card recipient email'],
      // Controlla il titolo
      item.title && item.title.toLowerCase().includes('gift card')
    ];

    const isGift = checks.some(check => check === true);

    // Salva in cache
    if (cacheKey) {
      this.giftCardCache.set(cacheKey, isGift);
    }

    // Log solo la prima volta (quando non era in cache)
    if (this.debug) {
      console.log(`[GiftCardCart] Controllo se "${item.title}" è una gift card:`, {
        product_type: item.product_type,
        risultato: isGift
      });
    }

    return isGift;
  }

  hasGiftCards(cart) {
    if (!cart || !cart.items) return false;
    return cart.items.some(item => this.isGiftCard(item));
  }

  hasRegularProducts(cart) {
    if (!cart || !cart.items) return false;
    return cart.items.some(item => !this.isGiftCard(item));
  }

  validateCartAndShowUI(cart) {
    // Ricontrolla se la validazione è abilitata
    if (!this.checkIfValidationEnabled()) {
      this.removeCartBlockUI();
      return true;
    }
    
    const hasGift = this.hasGiftCards(cart);
    const hasRegular = this.hasRegularProducts(cart);

    this.log('Validazione carrello:', {
      hasGiftCards: hasGift,
      hasRegularProducts: hasRegular,
      items: cart.items.map(item => ({
        title: item.title,
        type: item.product_type,
        isGift: this.isGiftCard(item)
      }))
    });

    if (hasGift && hasRegular) {
      // Il carrello contiene sia gift card che prodotti normali
      this.showCartBlockUI(cart);
      return false;
    }
    
    // Rimuovi eventuale UI di blocco se il carrello è valido
    this.removeCartBlockUI();
    return true;
  }

  showCartBlockUI(cart) {
    // Controlla se siamo nella pagina del carrello
    const cartForm = document.querySelector('#main-cart-form');
    const cartContainer = document.querySelector('.main-cart');
    
    if (!cartForm && !cartContainer) {
      // Non siamo nella pagina del carrello, mostra solo notifica
      this.showMixedCartNotification();
      return;
    }

    // Verifica se UI già presente per evitare duplicati
    if (document.getElementById('gift-card-block-message')) {
      return;
    }

    // Crea il messaggio di blocco
    const blockMessage = document.createElement('div');
    blockMessage.id = 'gift-card-block-message';
    blockMessage.className = 'alert alert-warning border-warning border-2 my-4';
    blockMessage.innerHTML = `
      <div class="d-flex align-items-start gap-3">
        <svg class="flex-shrink-0" width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
          <path d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0zM7.002 1.125a6.875 6.875 0 1 1 0 13.75 6.875 6.875 0 0 1 0-13.75zM7.25 4v5.25h1.5V4h-1.5zm0 6.5v1.5h1.5v-1.5h-1.5z"/>
        </svg>
        <div class="flex-grow-1">
          <h5 class="alert-heading mb-2">${this.translations.cartConflictTitle}</h5>
          <p class="mb-3">${this.translations.cartConflictMessage}</p>
          
          <div class="d-flex flex-column flex-sm-row gap-2">
            <button type="button" class="btn btn-warning" onclick="window.giftCardCart.removeRegularProducts()">
              ${this.translations.removeRegularProducts}
            </button>
            <button type="button" class="btn btn-outline-warning" onclick="window.giftCardCart.removeGiftCards()">
              ${this.translations.removeGiftCards}
            </button>
          </div>
        </div>
      </div>
    `;

    // Inserisci il messaggio all'inizio del carrello
    const cartTitle = document.querySelector('.main-cart h2, .main-cart h1');
    if (cartTitle) {
      cartTitle.parentNode.insertBefore(blockMessage, cartTitle.nextSibling);
    } else if (cartContainer) {
      cartContainer.insertBefore(blockMessage, cartContainer.firstChild);
    }

    // Disabilita il pulsante di checkout
    this.disableCheckoutButtons();

    // Evidenzia i prodotti problematici
    this.highlightConflictingItems(cart);
  }

  removeCartBlockUI() {
    const blockMessage = document.getElementById('gift-card-block-message');
    if (blockMessage) {
      blockMessage.remove();
    }
    
    // Riabilita i pulsanti di checkout
    this.enableCheckoutButtons();
    
    // Rimuovi evidenziazione prodotti
    this.removeItemHighlights();
  }

  disableCheckoutButtons() {
    const checkoutButtons = document.querySelectorAll(
      'button[type="submit"][name="checkout"], ' +
      'button[name="checkout"], ' +
      'a[href*="/checkout"], ' +
      '.checkout-button, ' +
      '.btn-checkout'
    );
    
    checkoutButtons.forEach(button => {
      // Evita di disabilitare più volte
      if (button.dataset.giftCardDisabled === 'true') return;
      
      button.setAttribute('disabled', 'disabled');
      button.classList.add('disabled', 'opacity-50');
      button.style.pointerEvents = 'none';
      button.dataset.giftCardDisabled = 'true';
      
      // Aggiungi tooltip
      button.setAttribute('title', this.translations.resolveConflictBeforeCheckout);
    });
  }

  enableCheckoutButtons() {
    const checkoutButtons = document.querySelectorAll(
      'button[type="submit"][name="checkout"], ' +
      'button[name="checkout"], ' +
      'a[href*="/checkout"], ' +
      '.checkout-button, ' +
      '.btn-checkout'
    );
    
    checkoutButtons.forEach(button => {
      if (button.dataset.giftCardDisabled === 'true') {
        button.removeAttribute('disabled');
        button.classList.remove('disabled', 'opacity-50');
        button.style.pointerEvents = '';
        button.removeAttribute('title');
        button.dataset.giftCardDisabled = 'false';
      }
    });
  }

  highlightConflictingItems(cart) {
    // Evidenzia visivamente i prodotti in conflitto
    cart.items.forEach((item, index) => {
      const itemElement = document.querySelector(`[data-variant-id="${item.variant_id}"]`) ||
                         document.querySelector(`[data-product-id="${item.product_id}"]`) ||
                         document.querySelectorAll('.cart-item')[index];
      
      if (itemElement) {
        const isGift = this.isGiftCard(item);
        if (isGift) {
          itemElement.classList.add('gift-card-item', 'border-warning', 'border-2');
          
          // Aggiungi badge
          const badge = document.createElement('span');
          badge.className = 'badge bg-warning text-dark ms-2';
          badge.textContent = 'Gift Card';
          
          const title = itemElement.querySelector('.cart-item__title, h4, h5');
          if (title && !title.querySelector('.badge')) {
            title.appendChild(badge);
          }
        }
      }
    });
  }

  removeItemHighlights() {
    document.querySelectorAll('.gift-card-item').forEach(item => {
      item.classList.remove('gift-card-item', 'border-warning', 'border-2');
    });
    
    document.querySelectorAll('.cart-item .badge').forEach(badge => {
      if (badge.textContent === 'Gift Card') {
        badge.remove();
      }
    });
  }

  showMixedCartNotification() {
    this.showNotification(this.translations.mixedCartWarning, 'warning', true, true);
  }

  showNotification(message, type = 'info', persistent = false, showCartButton = false) {
    // Rimuovi notifiche esistenti
    document.querySelectorAll('.gift-card-notification').forEach(n => n.remove());
    
    // Crea una notifica toast più visibile
    const notification = document.createElement('div');
    notification.className = `gift-card-notification alert alert-${type === 'error' ? 'danger' : type} position-fixed start-50 translate-middle-x shadow-lg border-2`;
    notification.style.cssText = 'z-index: 99999; min-width: 350px; max-width: 500px; top: 100px; animation: slideDown 0.3s ease-out;';
    
    let buttonsHtml = '';
    if (showCartButton) {
      buttonsHtml = `
        <div class="mt-3 d-flex gap-2">
          <a href="/cart" class="btn btn-sm btn-${type === 'error' ? 'danger' : 'warning'}">${this.translations.goToCart}</a>
          <button type="button" class="btn btn-sm btn-outline-secondary" onclick="this.closest('.gift-card-notification').remove()">${this.translations.close}</button>
        </div>
      `;
    }
    
    notification.innerHTML = `
      <div class="d-flex justify-content-between align-items-start">
        <div class="flex-grow-1">
          <div class="fw-bold mb-1">${message}</div>
          ${buttonsHtml}
        </div>
        <button type="button" class="btn-close ms-3" aria-label="Close"></button>
      </div>
    `;
    
    // Aggiungi animazione CSS
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideDown {
        from {
          opacity: 0;
          transform: translateX(-50%) translateY(-20px);
        }
        to {
          opacity: 1;
          transform: translateX(-50%) translateY(0);
        }
      }
    `;
    if (!document.querySelector('#gift-card-notification-styles')) {
      style.id = 'gift-card-notification-styles';
      document.head.appendChild(style);
    }
    
    document.body.appendChild(notification);
    
    // Event listeners
    const closeBtn = notification.querySelector('.btn-close');
    closeBtn.addEventListener('click', () => notification.remove());
    
    // Auto-rimuovi dopo un tempo appropriato
    const timeout = type === 'error' ? 10000 : (persistent ? 15000 : 5000);
    setTimeout(() => {
      if (notification.parentNode) {
        notification.style.animation = 'slideDown 0.3s ease-out reverse';
        setTimeout(() => notification.remove(), 300);
      }
    }, timeout);
  }

  async removeGiftCards() {
    try {
      const cart = await this.getCart();
      if (!cart || !cart.items) return;

      const giftCardItems = cart.items.filter(item => this.isGiftCard(item));

      this.log('Rimozione gift card:', giftCardItems);

      if (giftCardItems.length > 0) {
        const updates = {};
        giftCardItems.forEach(item => {
          updates[item.key] = 0;
        });

        // Invalida cache prima dell'update
        this.invalidateCache();

        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ updates })
        });

        if (response.ok) {
          window.location.reload();
        } else {
          this.showNotification(this.translations.errorRemovingGiftCards, 'error');
        }
      }
    } catch (error) {
      console.error('Errore nella rimozione delle gift card:', error);
      this.showNotification(this.translations.errorRemovingGiftCards, 'error');
    }
  }

  async removeRegularProducts() {
    try {
      const cart = await this.getCart();
      if (!cart || !cart.items) return;

      const regularItems = cart.items.filter(item => !this.isGiftCard(item));

      this.log('Rimozione prodotti normali:', regularItems);

      if (regularItems.length > 0) {
        const updates = {};
        regularItems.forEach(item => {
          updates[item.key] = 0;
        });

        // Invalida cache prima dell'update
        this.invalidateCache();

        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ updates })
        });

        if (response.ok) {
          window.location.reload();
        } else {
          this.showNotification(this.translations.errorRemovingProducts, 'error');
        }
      }
    } catch (error) {
      console.error('Errore nella rimozione dei prodotti normali:', error);
      this.showNotification(this.translations.errorRemovingProducts, 'error');
    }
  }

  observeCartChanges() {
    // Osserva cambiamenti nel DOM del carrello con debounce
    let debounceTimer;
    const observer = new MutationObserver(() => {
      // Evita loop infiniti
      if (this.isProcessing) return;

      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        // Invalida cache prima di ricontrollare
        this.invalidateCache();
        this.checkCartOnLoad();
      }, 300); // Debounce di 300ms
    });

    const cartElement = document.querySelector('.main-cart, #main-cart, [data-cart-items]');
    if (cartElement) {
      observer.observe(cartElement, {
        childList: true,
        subtree: false // Solo figli diretti per ridurre trigger
      });
    }
  }

  // Metodo helper per aggiornare il contatore del carrello
  async updateCartCounter() {
    try {
      // Usa cache se disponibile, altrimenti fetch
      const cart = await this.getCart();
      const counters = document.querySelectorAll('.cart-count, .cart-counter, [data-cart-count]');
      counters.forEach(counter => {
        counter.textContent = cart.item_count || 0;
      });
    } catch (error) {
      console.error('Errore aggiornamento contatore carrello:', error);
    }
  }
}

// Inizializza quando il DOM è pronto
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.giftCardCart = new GiftCardCart();
  });
} else {
  window.giftCardCart = new GiftCardCart();
}

// Esporta per uso esterno
window.GiftCardCart = GiftCardCart;